import { useMutation } from "@tanstack/react-query";
import { supabase } from "../supabase/supabase-client";
import { LogeMember } from "../types/entities";

// Email + one-time-code auth. signInWithOtp both sends the code and (if the
// email hasn't signed in before) silently creates the auth.users row — the
// same call covers "login" and "first-time signup" from Supabase's side;
// this app's own /signup step is about claiming a loge_members roster row,
// not creating the auth account.
//
// Careful with that "same call" though: GoTrue only *routes* it the same
// way. For an email it has never seen it hands the request to its signup
// path, which mails the "Confirm signup" template (and mails nothing at all
// when email confirmations are switched off, while still answering 200 OK).
// Only the second request — the address now exists — takes the magic-link
// path that mails a 6-digit code. That asymmetry is what makes a first
// login look like it silently did nothing; see supabase/config.toml for the
// project settings that have to hold for the first mail to carry a code.

/** GoTrue lowercases addresses server-side; trim so send and verify agree. */
const normalizeEmail = (email: string) => email.trim().toLowerCase();

// The pinned gotrue-js builds AuthApiError with `message` and `status`
// only — no `code` — so every branch below has to be reachable by status or
// message alone. The `code` checks are kept for when that dependency moves;
// they are additive, never the only route to a message.
type SupabaseAuthError = { code?: string; status?: number; message?: string };

const asAuthError = (error: unknown): SupabaseAuthError =>
  typeof error === "object" && error !== null ? (error as SupabaseAuthError) : {};

/**
 * Whether GoTrue turned the request away for asking too often.
 *
 * Two very different limits both surface as a 429, and the wait they imply
 * is nothing alike:
 *
 *   * the per-address gap between two mails — tens of seconds, and GoTrue
 *     names the exact number in its message
 *   * the project-wide cap on mails per hour — every member shares one
 *     budget, so once it is spent nobody can log in until the hour rolls
 *
 * Telling them apart matters: quoting a minute or two at someone who is
 * actually locked out for the rest of the hour just makes them retry.
 */
export const isRateLimitError = (error: unknown): boolean => {
  const { code, status } = asAuthError(error);
  return (
    status === 429 ||
    code === "over_email_send_rate_limit" ||
    code === "over_request_rate_limit"
  );
};

/**
 * Whether the server could not send the mail at all — "Error sending
 * confirmation email", a 500 with an error_id and nothing a member can act
 * on. It means the project's SMTP settings are wrong (bad credentials, an
 * unverified sender), so it will fail identically forever until an admin
 * fixes them. Telling someone to try again later is a lie in that state.
 */
export const isMailerFailure = (error: unknown): boolean => {
  const { status, message } = asAuthError(error);
  return status === 500 || /error sending/i.test(message ?? "");
};

/**
 * Whether the request died on the way rather than being answered.
 *
 * This matters more than it looks. GoTrue hands the mail to SMTP *inside*
 * the /otp request and waits for the handoff, so a hanging SMTP
 * conversation hangs the request until the gateway gives up with a 504.
 * The client cannot tell how far it got: the mail may be in the member's
 * inbox already, or may never have left. Treating that as a flat failure
 * strands someone on the address screen with a working code in their mail.
 */
export const isGatewayTimeout = (error: unknown): boolean =>
  asAuthError(error).status === 504;

/**
 * The wait GoTrue names in a per-address throttle ("For security purposes,
 * you can only request this after 47 seconds."), or null when the 429 came
 * from the project's hourly budget instead — that one carries no number,
 * because the answer is "whenever the hour rolls".
 */
export const rateLimitRetryAfterSeconds = (error: unknown): number | null => {
  if (!isRateLimitError(error)) return null;
  const match = /after (\d+) seconds?/i.exec(asAuthError(error).message ?? "");
  return match ? Number(match[1]) : null;
};

/**
 * A user-facing reason for a failed send.
 *
 * The screen used to collapse every failure into "check the mail address",
 * which is a big part of why "I never got a code" went undiagnosed for so
 * long — a rate limit and a disabled email provider both read as a typo.
 */
export const describeOtpSendError = (error: unknown): string => {
  const { code, status } = asAuthError(error);
  if (isRateLimitError(error)) {
    const wait = rateLimitRetryAfterSeconds(error);
    if (wait !== null) {
      return `Der er lige sendt en kode til dig. Vent ${wait} sekunder, og prøv så igen.`;
    }
    // No number means the project's hourly mail budget is spent, and it is
    // shared: this member may have done nothing wrong at all.
    return "Appen kan ikke sende flere mails i denne time. Prøv igen senere, eller sig til en admin.";
  }
  if (code === "email_provider_disabled" || code === "signup_disabled") {
    return "Login med mail er slået fra på serveren. Kontakt admin.";
  }
  if (code === "otp_disabled") {
    return "Engangskoder er slået fra på serveren. Kontakt admin.";
  }
  if (code === "validation_failed" || status === 422) {
    return "Mailadressen ser forkert ud.";
  }
  if (isGatewayTimeout(error)) {
    return "Serveren svarede ikke i tide. Koden er muligvis sendt alligevel — tjek din mail, før du beder om en ny.";
  }
  if (isMailerFailure(error)) {
    return "Serveren kan ikke sende mails lige nu (opsætningsfejl). Sig til en admin — det hjælper ikke at prøve igen.";
  }
  return "Kunne ikke sende koden. Prøv igen om lidt.";
};

/**
 * Marks the one verification failure GoTrue does not report as an error:
 * /verify answering 200 with no session in the body. Nothing threw, so the
 * screen counted it as a login, navigated, and RequireAuth — finding no
 * session — sent the member back to the address form. From the outside
 * that looked like the code had simply been ignored.
 */
export const NO_SESSION_RETURNED = "no_session_returned";

/** A user-facing reason for a failed verification. */
export const describeOtpVerifyError = (error: unknown): string => {
  const { code } = asAuthError(error);
  if (code === NO_SESSION_RETURNED) {
    return "Kunne ikke logge dig ind. Koden blev godkendt, men der kom ingen session retur — bed om en ny kode, eller sig til en admin.";
  }
  if (code === "otp_expired") {
    return "Koden er udløbet. Bed om en ny.";
  }
  if (isRateLimitError(error)) {
    const wait = rateLimitRetryAfterSeconds(error);
    return wait !== null
      ? `For mange forsøg. Vent ${wait} sekunder, og prøv så igen.`
      : "For mange forsøg lige nu. Prøv igen om lidt.";
  }
  return "Forkert eller udløbet kode.";
};

export const useSendOtp = () => {
  return useMutation(async (email: string) => {
    const { error } = await supabase.auth.signInWithOtp({
      email: normalizeEmail(email),
      options: { shouldCreateUser: true },
    });
    if (error) {
      // The mapped message is what the user reads; keep the raw error where
      // whoever is debugging the next report can actually see it.
      console.error("signInWithOtp failed", error);
      throw error;
    }
  });
};

export interface VerifyOtpPayload {
  email: string;
  token: string;
}

export const useVerifyOtp = () => {
  return useMutation(async ({ email, token }: VerifyOtpPayload) => {
    const { data, error } = await supabase.auth.verifyOtp({
      email: normalizeEmail(email),
      token: token.trim(),
      // "email" covers both token kinds: the confirmation token a brand-new
      // address gets from the signup path, and the magic-link token every
      // later request gets. Narrowing this to "magiclink" would reject the
      // first-ever code.
      type: "email",
    });
    if (error) {
      console.error("verifyOtp failed", error);
      throw error;
    }
    // A 200 with no session is not a login, however much it looks like one.
    if (!data?.session) {
      console.error("verifyOtp returned no session", data);
      throw Object.assign(new Error("Verification returned no session."), {
        code: NO_SESSION_RETURNED,
      });
    }
    return data;
  });
};

// Links the signed-in auth user to a chosen, currently-unclaimed roster row.
// RLS only allows this when the target row's auth_user_id is still null and
// the caller is setting it to their own auth.uid().
export const useClaimMember = () => {
  return useMutation(async (memberId: LogeMember["id"]) => {
    const { data: userData, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;
    const user = userData.user;
    if (!user) throw new Error("No signed-in user to claim a member for.");

    const { error } = await supabase
      .from("loge_members")
      .update({ auth_user_id: user.id, email: user.email })
      .eq("id", memberId);
    if (error) throw error;
  });
};
